import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Edit3,
  LoaderCircle,
  MapPin,
  Plus,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { APIError, api, postJSON } from '../api'
import { countries, countryName } from '../countries'
import type { AddressSuggestion, AddressValidation, Dashboard, Destination } from '../types'

const blank = {
  label: 'Primary destination',
  recipientName: '',
  company: '',
  line1: '',
  line2: '',
  line3: '',
  streetNumber: '',
  dependentLocality: '',
  city: '',
  subdivision: '',
  postalCode: '',
  countryCode: '',
  phone: '',
  email: '',
  formattedAddress: '',
  addressLanguage: '',
  captureProvider: 'manual',
  captureProviderId: '',
  capturePrecision: '',
  captureReceiptId: '',
  acceptOriginal: false,
  isDefault: true,
}

type DestinationForm = typeof blank

export function Destinations() {
  const [items, setItems] = useState<Destination[]>([])
  const [form, setForm] = useState<DestinationForm>(blank)
  const [adding, setAdding] = useState(false)
  const [manual, setManual] = useState(false)
  const [selected, setSelected] = useState(false)
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [validation, setValidation] = useState<AddressValidation | null>(null)
  const searchSequence = useRef(0)

  useEffect(() => {
    api<Dashboard>('/api/dashboard')
      .then(value => setItems(value.destinations))
      .catch(caught => setError(caught instanceof Error ? caught.message : 'Could not load destinations'))
  }, [])

  const findSuggestions = useCallback(async (containerId = '') => {
    const sequence = ++searchSequence.current
    setSearching(true)
    setError('')
    try {
      const result = await postJSON<{ suggestions: AddressSuggestion[] }>('/api/addresses/find', {
        text: query,
        countryCode: form.countryCode,
        containerId,
      })
      if (sequence === searchSequence.current) setSuggestions(result.suggestions)
    } catch (caught) {
      if (sequence === searchSequence.current) setError(caught instanceof Error ? caught.message : 'Address search failed')
    } finally {
      if (sequence === searchSequence.current) setSearching(false)
    }
  }, [form.countryCode, query])

  useEffect(() => {
    if (!adding || manual || selected || query.trim().length < 3 || !form.countryCode) {
      setSuggestions([])
      return
    }
    const timer = window.setTimeout(() => void findSuggestions(), 350)
    return () => window.clearTimeout(timer)
  }, [query, form.countryCode, adding, manual, selected, findSuggestions])

  const chooseSuggestion = async (suggestion: AddressSuggestion) => {
    if (suggestion.type.toLowerCase() !== 'address') {
      await findSuggestions(suggestion.id)
      return
    }
    setSearching(true)
    setError('')
    try {
      const address = await postJSON<Partial<DestinationForm>>('/api/addresses/retrieve', { id: suggestion.id })
      setForm(current => ({ ...current, ...address, countryCode: address.countryCode || current.countryCode, acceptOriginal: false }))
      setSelected(true)
      setManual(false)
      setSuggestions([])
      setValidation(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not retrieve that address')
    } finally {
      setSearching(false)
    }
  }

  const validateAndSave = (event: FormEvent) => {
    event.preventDefault()
    void save(form, false)
  }

  const save = async (value: DestinationForm, acceptOriginal: boolean) => {
    setBusy(true)
    setError('')
    if (!acceptOriginal) setValidation(null)
    try {
      const item = await postJSON<Destination>('/api/destinations', { ...value, acceptOriginal })
      setItems(current => [item, ...current.map(existing => ({ ...existing, isDefault: item.isDefault ? false : existing.isDefault }))])
      closeForm()
    } catch (caught) {
      const review = caught instanceof APIError ? caught.data?.validation as AddressValidation | undefined : undefined
      if (review) setValidation(review)
      else setError(caught instanceof Error ? caught.message : 'Could not save destination')
    } finally {
      setBusy(false)
    }
  }

  const applySuggested = async () => {
    if (!validation?.recommended) return
    const suggested = { ...form, ...validation.recommended, acceptOriginal: false }
    setForm(suggested)
    await save(suggested, false)
  }

  const closeForm = () => {
    searchSequence.current += 1
    setAdding(false)
    setManual(false)
    setSelected(false)
    setQuery('')
    setSuggestions([])
    setValidation(null)
    setForm(blank)
  }

  const startManual = () => {
    searchSequence.current += 1
    setManual(true)
    setSelected(false)
    setSuggestions([])
    setValidation(null)
    setForm(current => ({ ...current, captureProvider: 'manual', captureProviderId: '', capturePrecision: '', captureReceiptId: '', formattedAddress: '', addressLanguage: '' }))
  }

  const changeCountry = (countryCode:string) => {
    searchSequence.current += 1
    setForm(current => ({
      ...blank,
      label: current.label,
      recipientName: current.recipientName,
      phone: current.phone,
      email: current.email,
      countryCode,
    }))
    setQuery('')
    setSelected(false)
    setManual(false)
    setSuggestions([])
    setValidation(null)
  }

  const showDetails = selected || manual

  return <div className="page">
    <Link className="back-link" to="/dashboard"><ArrowLeft size={16}/>Overview</Link>
    <header className="page-head">
      <div>
        <div className="eyebrow"><span/> ADDRESS BOOK</div>
        <h1>Destinations</h1>
        <p>Search in the local format. We will structure the result for the carrier before you save it.</p>
      </div>
      <button className={`button ${adding ? 'button--outline' : 'button--primary'}`} onClick={() => adding ? closeForm() : setAdding(true)}>{adding ? <X size={17}/> : <Plus size={17}/>} {adding ? 'Cancel' : 'Add destination'}</button>
    </header>
    {error && <div className="form-error" role="alert">{error}</div>}

    {adding && <form className="address-form address-capture" onSubmit={validateAndSave}>
      <div className="address-capture__head">
        <div>
          <span className="mono-label">NEW DESTINATION</span>
          <h2>Where should we deliver?</h2>
          <p>Choose the country first, then type the address as you normally would.</p>
        </div>
        <ol aria-label="Destination setup progress">
          <li className="is-active"><b>01</b> Find</li>
          <li className={showDetails ? 'is-active' : ''}><b>02</b> Details</li>
          <li className={validation ? 'is-active' : ''}><b>03</b> Verify</li>
        </ol>
      </div>

      <section className="capture-search" aria-labelledby="find-address-title">
        <div className="capture-step"><span>01</span><div><b id="find-address-title">Find the address</b><small>International search, tuned to the selected country</small></div></div>
        <label>Country or territory
          <select value={form.countryCode} onChange={event => changeCountry(event.target.value)} required>
            <option value="">Choose a country</option>
            {countries.map(country => <option value={country.code} key={country.code}>{country.name}</option>)}
          </select>
        </label>
        {!manual && !selected && <div className="address-combobox">
          <label htmlFor="address-search">Start typing the address</label>
          <div className="address-combobox__input">
            <Search size={18}/>
            <input
              id="address-search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              disabled={!form.countryCode}
              placeholder={form.countryCode ? 'Street, building, city, or postal code' : 'Choose a country first'}
              role="combobox"
              aria-expanded={suggestions.length > 0}
              aria-controls="address-suggestions"
              aria-autocomplete="list"
              autoComplete="off"
            />
            {searching && <LoaderCircle className="spin" aria-label="Searching"/>}
          </div>
          {suggestions.length > 0 && <div className="address-suggestions" id="address-suggestions" role="listbox">
            {suggestions.map(suggestion => <button type="button" role="option" key={`${suggestion.id}-${suggestion.type}`} onClick={() => void chooseSuggestion(suggestion)}>
              <MapPin size={17}/><span><b>{suggestion.text}</b><small>{suggestion.description}</small></span><ChevronRight size={16}/>
            </button>)}
          </div>}
          {query.length >= 3 && !searching && suggestions.length === 0 && <p className="capture-hint">No exact result yet. Add a city or postal code, or enter it manually.</p>}
        </div>}
        {selected && <div className="capture-match">
          <i><CheckCircle2/></i>
          <div><span className="mono-label">STREET ADDRESS FOUND</span><b>{formatAddress(form)}</b><small>Review apartment, unit, and contact details below</small></div>
          <button type="button" className="text-button" onClick={() => { setSelected(false); setQuery(''); setValidation(null) }}>Search again</button>
        </div>}
        {!selected && <button className="manual-entry-link" type="button" onClick={manual ? () => { setManual(false); setQuery('') } : startManual}><Edit3 size={15}/>{manual ? 'Use address search instead' : 'Enter the address manually'}</button>}
      </section>

      {showDetails && <section className="capture-details" aria-labelledby="address-details-title">
        <div className="capture-step"><span>02</span><div><b id="address-details-title">Review delivery details</b><small>Keep apartment, unit, floor, and building details exactly as used locally</small></div></div>
        <div className="form-grid">
          <label>Address line 1<input autoComplete="address-line1" value={form.line1} onChange={event => setForm({ ...form, line1: event.target.value })} required/></label>
          <label>Address line 2 <small>Apartment, unit, floor</small><input autoComplete="address-line2" value={form.line2} onChange={event => setForm({ ...form, line2: event.target.value })}/></label>
          <label>Address line 3 <small>Optional</small><input autoComplete="address-line3" value={form.line3} onChange={event => setForm({ ...form, line3: event.target.value })}/></label>
          <label>City / locality<input autoComplete="address-level2" value={form.city} onChange={event => setForm({ ...form, city: event.target.value })} required/></label>
          <label>State / province / region <small>When used locally</small><input autoComplete="address-level1" value={form.subdivision} onChange={event => setForm({ ...form, subdivision: event.target.value })}/></label>
          <label>Postal code <small>When used locally</small><input autoComplete="postal-code" value={form.postalCode} onChange={event => setForm({ ...form, postalCode: event.target.value })}/></label>
        </div>

        <div className="contact-divider"><span>RECIPIENT</span></div>
        <div className="form-grid">
          <label>Full name<input autoComplete="name" value={form.recipientName} onChange={event => setForm({ ...form, recipientName: event.target.value })} required/></label>
          <label>Company <small>Optional</small><input autoComplete="organization" value={form.company} onChange={event => setForm({ ...form, company: event.target.value })}/></label>
          <label>Phone number<input type="tel" autoComplete="tel" value={form.phone} onChange={event => setForm({ ...form, phone: event.target.value })} required/></label>
          <label>Email<input type="email" autoComplete="email" value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} required/></label>
          <label>Save as<input value={form.label} onChange={event => setForm({ ...form, label: event.target.value })}/></label>
        </div>
      </section>}

      {validation && <AddressReview validation={validation} entered={form} onSuggested={() => void applySuggested()} onOriginal={() => void save(form, true)} onRetry={() => void save(form, false)} busy={busy}/>}

      {showDetails && !validation && <div className="form-actions form-actions--capture">
        <button type="button" className="button button--ghost" onClick={closeForm}>Cancel</button>
        <button className="button button--primary" disabled={busy}>{busy ? <><LoaderCircle className="spin" size={17}/>Checking…</> : <><ShieldCheck size={17}/>Check and save</>}</button>
      </div>}
    </form>}

    <div className="address-list">
      {items.map(item => <article className="address-card" key={item.id}>
        <i><MapPin/></i>
        <div>
          <div className="address-card__title"><h2>{item.label}</h2>{item.isDefault && <span><Check size={13}/>Default</span>}</div>
          <address>{item.recipientName}<br/>{item.line1}<br/>{item.line2 && <>{item.line2}<br/></>}{item.line3 && <>{item.line3}<br/></>}{item.city}{item.subdivision ? `, ${item.subdivision}` : ''} {item.postalCode}<br/>{countryName(item.countryCode)}</address>
          <small>{item.phone} · {item.email}</small>
          <span className={`address-confidence address-confidence--${item.validationStatus}`}><ShieldCheck size={13}/>{validationLabel(item.validationStatus)}</span>
        </div>
      </article>)}
      {!items.length && !adding && <div className="panel-empty panel-empty--large"><MapPin/><b>No destinations saved</b><p>Add the international address where your package should go.</p></div>}
    </div>
  </div>
}

function AddressReview({ validation, entered, onSuggested, onOriginal, onRetry, busy }:{ validation:AddressValidation; entered:DestinationForm; onSuggested:()=>void; onOriginal:()=>void; onRetry:()=>void; busy:boolean }) {
  const hasSuggestion = validation.status === 'suggested' && validation.recommended
  const unavailable = validation.status === 'unavailable'
  const unsupported = validation.status === 'unsupported'
  const heading = hasSuggestion
    ? 'Shippo found a carrier-ready version.'
    : unavailable
      ? 'Shippo is temporarily unavailable.'
      : unsupported
        ? `Shippo does not validate addresses in ${countryName(entered.countryCode)}.`
        : 'Shippo could not validate this address.'
  const guidance = hasSuggestion
    ? 'Compare both versions. We will only use the one you choose.'
    : unavailable
      ? 'Try the carrier check again, or save your reviewed entry manually.'
      : 'Review every line, then confirm to save.'
  return <section className={`address-review address-review--${validation.status}`} aria-live="polite">
    <header><CircleAlert/><div><span className="mono-label">FINAL ADDRESS CHECK</span><h3>{heading}</h3><p>{guidance}</p></div></header>
    {hasSuggestion ? <div className="address-compare">
      <article><span>YOU ENTERED</span><address>{formatAddress(entered)}</address><button type="button" className="button button--outline" disabled={busy} onClick={onOriginal}>Keep this version</button></article>
      <article className="address-compare__recommended"><span>SHIPPO SUGGESTS</span><address>{formatAddress(validation.recommended!)}</address><button type="button" className="button button--primary" disabled={busy} onClick={onSuggested}>Use suggested</button></article>
    </div> : <div className="address-confirm-row"><address>{formatAddress(entered)}</address><div className="address-confirm-actions">{unavailable&&<button type="button" className="button button--outline" disabled={busy} onClick={onRetry}>Try check again</button>}<button type="button" className="button button--primary" disabled={busy} onClick={onOriginal}>{busy ? 'Saving…' : 'Confirm and save'}</button></div></div>}
    {validation.messages && validation.messages.length > 0 && <small>{validation.messages.join(' ')}</small>}
  </section>
}

function formatAddress(address:Partial<DestinationForm>) {
  return [address.line1, address.line2, address.line3, [address.city, address.subdivision, address.postalCode].filter(Boolean).join(', '), address.countryCode ? countryName(address.countryCode) : ''].filter(Boolean).join(' · ')
}

function validationLabel(status:Destination['validationStatus']) {
  if (status === 'validated') return 'Format checked by Shippo'
  if (status === 'capture_only') return 'Address selected'
  if (status === 'customer_confirmed') return 'Customer confirmed'
  return 'Not yet checked'
}
